import { Skeleton, Switch } from 'antd'
import dayjs from 'dayjs'
import { useContext, useEffect, useState } from 'react'

import { API } from '@/api'
import { ServerDomainInfo, Volume } from '@/api/type-server'
import { Button, Divider, Loading, Trans } from '@/components'
import { AppContext } from '@/components/Layout'
import DriveVolumeSection from '@/pages/space-mgr/components/drive-volume-section.tsx'
import { useUserStore } from '@/stores/user'
import { formatToDynamicDecimalPlaces } from '@/utils/formatter'
import { cn } from '@/utils/style'

import ExpansionModal from './components/expansion-modal'
import SnapVolumeSection from './components/snap-volume-section.tsx'
import VolumeQuotaModal from './components/volume-quota-modal'
import { VolumeUsageSegmentedBar } from './components/volume-usage-segmented-bar'

export function SpaceMgrPage() {
  const userStore = useUserStore()
  const [expansionModalOpen, setExpansionModalOpen] = useState(false)
  const [volumeQuotaModalOpen, setVolumeQuotaModalOpen] = useState(false)
  const [autoRenewal, setAutoRenewal] = useState(userStore.order?.autoRenewal || false)
  const [updatingAutoRenewal, setUpdatingAutoRenewal] = useState(false)

  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])
  const [snapshotVolumes, setSnapshotVolumes] = useState<Volume[]>()
  const [cloudVolumes, setCloudVolumes] = useState<Volume[]>()

  const [loading, setLoading] = useState(false)

  const app = useContext(AppContext)

  const getDomains = () => {
    setLoading(true)
    API.getDomainList()
      .then((res: any) => {
        if (res.code === 0) {
          // todo: remove this after the backend is fixed
          const filteredDomainInfos = res.domainInfos.filter((info: any) => info.domain !== 3)
          setDomainInfos(filteredDomainInfos)
        }
      })
      .finally(() => {
        setLoading(false)
      })
  }

  const loadData = async () => {
    setLoading(true)
    try {
      const response = await API.getVolumes()
      if (response?.code == 0) {
        const volumes = response?.volumes || []

        setSnapshotVolumes(volumes.filter((it: any) => it.type == 2))
        setCloudVolumes(volumes.filter((it: any) => it.type != 2))
      }

      await Promise.all([userStore.getVolume(), userStore.getOrder()])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    getDomains()
    loadData()
    app.refresh()
  }, [])

  const handleAutoRenewalToggle = async (checked: boolean) => {
    setUpdatingAutoRenewal(true)
    try {
      await API.updateOrderAutoRenewal({
        auto_renewal: checked,
        catalog: 1,
        type: 1,
      })
      setAutoRenewal(checked)
      await userStore.getOrder()
    } catch (error) {
      console.error('Failed to update auto renewal:', error)
      setAutoRenewal(!checked)
    } finally {
      setUpdatingAutoRenewal(false)
    }
  }

  useEffect(() => {
    if (userStore.order?.autoRenewal !== undefined) {
      setAutoRenewal(userStore.order.autoRenewal)
    }
  }, [userStore.order?.autoRenewal])

  const partVolumeUsageInGB = (userStore.partVolume?.usage_mb || 0) / 1024
  const partVolumeQuotaInGB = userStore.isOrderExpired
    ? userStore.partVolume?.last_quota_gb || 0
    : userStore.partVolume?.quota_gb || 0

  return (
    <div className="flex flex-col h-full">
      <div className="card bg-white flex-shrink-0">
        <div className="flex items-center justify-between">
          <div className="h6">
            <Trans i18nKey="space.titleSpaceStorage">Storage Capacity</Trans>
          </div>
          {!userStore.isOrderExpired && (
            <div className="flex items-center gap-2">
              <span className="t-sb">Auto Renewal</span>
              <Switch
                checked={autoRenewal}
                loading={updatingAutoRenewal}
                onChange={handleAutoRenewalToggle}
                checkedChildren="ON"
                unCheckedChildren="OFF"
              />
            </div>
          )}
        </div>

        <div className="my-4">
          <VolumeUsageSegmentedBar />
        </div>

        <div className="flex items-center justify-between">
          <div className="flex gap-4 items-center">
            <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-gray-200">
              <div className="text-primary t-sb">
                <Trans
                  i18nKey="space.titleUsage"
                  values={{
                    usage: formatToDynamicDecimalPlaces(partVolumeUsageInGB),
                    quota: formatToDynamicDecimalPlaces(partVolumeQuotaInGB),
                  }}
                  components={[
                    <span className="text-secondary">Using </span>,
                    <span className="text-secondary"> of </span>,
                  ]}
                >
                  Using {{ usage: formatToDynamicDecimalPlaces(partVolumeUsageInGB) }}GiB of{' '}
                  {{ quota: formatToDynamicDecimalPlaces(partVolumeQuotaInGB) }}GiB
                </Trans>
              </div>
            </div>

            {userStore.order && (
              <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-gray-200">
                <div className="text-primary t-sb flex items-center space-x-1">
                  <div className="text-secondary">
                    <Trans i18nKey="space.expireOn">Expires on</Trans>
                  </div>
                  <div>
                    {userStore.order?.deadlineTime?.seconds ? (
                      dayjs(userStore.order.deadlineTime.seconds * 1000).format('YYYY-MM-DD')
                    ) : (
                      <Skeleton.Input active size="small" style={{ width: 100 }} />
                    )}
                  </div>
                </div>
              </div>
            )}

            {userStore.isOrderExpired && (
              <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-gray-200">
                <div className="text-primary t-sb flex items-center space-x-1">
                  <div className="text-secondary">Data Purge on</div>
                  <div className="text-error">
                    {userStore.order?.extra?.purgeTime?.seconds ? (
                      dayjs(userStore.order.extra.purgeTime.seconds * 1000).format('YYYY-MM-DD')
                    ) : (
                      <Skeleton.Input active size="small" style={{ width: 100 }} />
                    )}
                  </div>
                </div>
              </div>
            )}

            <div
              className="text-btn-secondary font-bold cursor-pointer t-sb"
              onClick={() => setExpansionModalOpen(true)}
            >
              <Trans i18nKey="space.upgrade">Upgrade</Trans>
            </div>
          </div>
          {partVolumeUsageInGB > 0 && (
            <div>
              <div className="flex items-baseline t-sm text-primary gap-2">
                <div
                  className={cn(
                    ' w-3 h-3',
                    userStore.isOrderExpired ? 'bg-bar-tertiary' : ' bg-btn-tertiary',
                  )}
                />
                <Trans i18nKey="space.volumeItemSubTitleSnapshot">Snapshot</Trans>
                <div
                  className={cn(
                    ' w-3 h-3 ml-2',
                    userStore.isOrderExpired ? 'bg-bar-tertiary/60' : 'bg-btn-secondary',
                  )}
                />
                <Trans i18nKey="space.volumeItemSubTitleCloudStorage">Data Hub</Trans>
              </div>
            </div>
          )}
        </div>

        {userStore.isOrderExpired && (
          <div className={cn('t-sm ', 'text-error', 'mt-4')}>
            <Trans i18nKey="space.volumeItemExpired">
              Your storage capacity has expired, please upgrade your plan
            </Trans>
          </div>
        )}
      </div>

      <div className="card bg-white mt-2.5 h-full overflow-y-auto">
        <div className="flex h-[60px] items-start justify-between">
          <div className="h6">
            <Trans i18nKey="space.titleQuota">Quota</Trans>
          </div>

          {!userStore.isOrderExpired && (
            <Button
              type="secondary"
              disabled={!partVolumeQuotaInGB || userStore.isOrderExpired}
              onClick={() => setVolumeQuotaModalOpen(true)}
            >
              <Trans i18nKey="space.modify">Modify</Trans>
            </Button>
          )}
        </div>
        <Divider />

        {loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : (
          <div className="mb-2 flex flex-col gap-8">
            <SnapVolumeSection volumes={snapshotVolumes || []} />
            <DriveVolumeSection volumes={cloudVolumes || []} domainInfos={domainInfos} />
          </div>
        )}
      </div>

      {expansionModalOpen ? (
        <ExpansionModal
          open={expansionModalOpen}
          onClose={() => setExpansionModalOpen(false)}
        ></ExpansionModal>
      ) : null}

      {volumeQuotaModalOpen ? (
        <VolumeQuotaModal
          open={volumeQuotaModalOpen}
          onOk={() => {
            setVolumeQuotaModalOpen(false)
            loadData()
            app.refresh()
          }}
          onClose={() => setVolumeQuotaModalOpen(false)}
          data={{
            snapshotVolumes: snapshotVolumes,
            cloudVolumes: cloudVolumes,
            domainInfos: domainInfos,
          }}
        ></VolumeQuotaModal>
      ) : null}
    </div>
  )
}
